import { createFileRoute } from '@tanstack/react-router';
import { FiCamera, FiDollarSign, FiPercent, FiRepeat } from 'react-icons/fi';
import { SeoLandingPage } from '@/components/marketing';
import harborSsExpenseReceiptPng from '@/assets/harbor_ss_expense_receipt.png';

function CoparentMoneyPage() {
  return (
    <SeoLandingPage
      path='/coparent-money'
      title='Co-Parent Expense Tracker & Shared Money | Harbor'
      description='Log child-related expenses, split reimbursements, and keep a running balance with receipts. Harbor tracks shared coparent money — not a child-support guideline calculator.'
      breadcrumbName='Coparent money'
      eyebrow='Shared expenses'
      h1='Track shared child expenses without the spreadsheet'
      hero='Soccer, prescriptions, and school fees pile up between two households. Harbor is a coparent expense tracker: log the cost, attach the receipt, split it, and see who owes whom. It is not a child-support formula.'
      screenEvent='Coparent Money Screen'
      ctaLocation='Coparent Money'
      storeCampaign='coparent-money'
      featuresHeading='Keep reimbursements obvious'
      features={[
        {
          icon: FiDollarSign,
          title: 'A running balance, not a lost Venmo thread',
          description:
            'Every child-related cost lands on one ledger. Harbor shows who paid, who owes, and what is still open so you are not reconstructing money from texts.',
          bullets: [
            'Log expenses in seconds as they happen',
            'See a running balance of who owes whom',
            'Keep categories for medical, school, activities, and more',
          ],
          image: harborSsExpenseReceiptPng,
          imageAlt: 'Harbor expense record with an attached receipt',
        },
        {
          icon: FiCamera,
          title: 'Receipts attached to the charge',
          description:
            'Snap the receipt when you pay. Harbor keeps the photo on the expense so “send me the receipt” is not a weekly argument.',
          bullets: [
            'Receipt photos on the expense record',
            'Amount, merchant, and date stay with the entry',
            'Export the paper trail with the rest of your records',
          ],
        },
        {
          icon: FiPercent,
          title: '50/50 or the split you actually use',
          description:
            'Default to even splits or set custom percentages when the order says something else. Harbor calculates the share; you do not do the math in Notes.',
          bullets: [
            'Automatic 50/50 or custom percentages',
            'Per-expense overrides when a cost is not shared evenly',
            'A clear owed amount instead of a vague “you got this one”',
          ],
        },
        {
          icon: FiRepeat,
          title: 'Expenses are not child support',
          description:
            'Guideline child support is a court worksheet. Shared extras — braces, cleats, field trips — are a different pile of money. Harbor tracks the extras; use the calculator if you need a support estimate.',
          bullets: [
            'Reimbursements and receipts, not a support formula',
            'Works if only one parent is logging costs',
            'Pair with the free state calculator when you need an estimate',
          ],
        },
      ]}
      faqs={[
        {
          question: 'Does Harbor calculate child support?',
          answer:
            'No. This page is for shared expenses and reimbursements — the extras two parents split. For a monthly support estimate, use Harbor’s free state child-support calculator. That tool is educational, not legal advice.',
        },
        {
          question: 'What kinds of coparent money can I track?',
          answer:
            'Uninsured medical costs, school fees, activities, clothing, and other child-related expenses you agreed to share. Attach a receipt and split the amount.',
        },
        {
          question: 'Can I use Harbor if my co-parent will not pay through the app?',
          answer:
            'Yes. Log the expense and keep the balance even if they reimburse outside Harbor. The record still shows what was shared and what is outstanding.',
        },
        {
          question: 'Can I export expense history for a lawyer?',
          answer:
            'Yes. Export expenses and receipts with messages and calendar records when you need a packet.',
        },
      ]}
      crossLinks={[
        {
          href: '/child-support-calculator',
          label: 'Child support calculator',
          description:
            'Need a monthly support estimate by state? That is a different tool from this shared-expense ledger.',
        },
        {
          href: '/court-ready-records',
          label: 'Court-ready records',
          description:
            'Export the expense ledger with receipts, messages, and calendar history.',
        },
        {
          href: '/coparenting-app-cost-calculator',
          label: 'Co-parenting app cost calculator',
          description:
            'Compare one- to five-year app costs before you pick a platform for the ledger.',
        },
      ]}
    />
  );
}

export const Route = createFileRoute('/coparent-money')({
  component: CoparentMoneyPage,
});
